import { Component, inject, Input, LOCALE_ID, OnDestroy, OnInit , signal } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { forkJoin, lastValueFrom, Observable, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { PipesService } from 'src/app/catalogs/shared/services/pipes-catalog.service';
import { GradesService } from 'src/app/catalogs/shared/services/grades-catalog.service';
import { GradeCatalog } from 'src/app/catalogs/models/grade-catalog.model';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { WellConfigService } from 'src/app/shared/services/well-config.service';
import { DrillstringService } from '../../services/drillstring.service';
import { DrillstringModel } from './drillstring.model';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { StorageKeys, SessionStorageKeys, StoreService } from 'src/app/core/services/store.service';
import { CurrentAppIdsDto } from 'src/app/core/models/current-app-ids-dto';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { GetShortLengthValueFromInches } from 'src/app/perivis/shared/helpers/units.helper';
import { PipesCatalog } from 'src/app/catalogs/models/pipes-catalog.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { formatUnitNumber } from 'src/app/shared/helpers/unit-number-format.helper';
import { UnitPrecisionOverrides } from 'src/app/shared/helpers/unit-precision.helper';

@Component({
  selector: 'app-drillstring',
  templateUrl: './drillstring.component.html',
  standalone: false
})
export class DrillstringComponent implements OnInit, OnDestroy {
  private _subscriptions = new Subscription();
  private _appIds: CurrentAppIdsDto;
  public drillstringId: string;
  private calledFromSignalR = false;
  private resetData = false;
  private _shortLengthUnit: string;
  private _linearDensityUnit: string;
  private _weightSymbol: string;
  private _diameterPrecisionOverrides: UnitPrecisionOverrides = {};
  private _weightPrecisionOverrides: UnitPrecisionOverrides = {};
  private readonly _locale = inject(LOCALE_ID);

  public isLoading = signal<boolean>(false);
  public drillstringForm: UntypedFormGroup;
  public drillPipes: PipesCatalog[] = [];
  public drillPipeGrades: GradeCatalog[] = [];
  public grades: GradeCatalog[] = [];
  public hwdps: PipesCatalog[] = [];
  public hwdpGrades: GradeCatalog[] = [];
  public collars: PipesCatalog[] = [];
  public collarGrades: GradeCatalog[] = [];
  public isTubingOrTieback = false;
  public disabledMessage: string;
  public configMissing = false;
  public longLengthUnit: string;

  public hwdpLengthValidation = { min: 0, max: 10000000 };
  public collarLengthValidation = { min: 0, max: 10000000 };

  @Input() private componentId: string;

  get isDialogVisible(): boolean {
    return this.configMissing;
  }

  public control(name: string): UntypedFormControl {
    return this.drillstringForm.get(name) as UntypedFormControl;
  }

  constructor(
    private _store: StoreService,
    private _formBuilder: UntypedFormBuilder,
    private _pipesCatalogService: PipesService,
    private _gradesCatalogService: GradesService,
    private _drillstringService: DrillstringService,
    private _signalRService: SignalRService,
    private _wellConfigService: WellConfigService,
    private _messenger: MediatorService
  ) {
    this.drillstringForm = this._formBuilder.group({
      tubularStringId: new UntypedFormControl(null),
      drillPipe: new UntypedFormControl(null),
      drillPipeGrade: new UntypedFormControl(null),
      heavyWeightDrillPipe: new UntypedFormControl(null),
      hwdpGrade: new UntypedFormControl(null),
      collar: new UntypedFormControl(null),
      collarGrade: new UntypedFormControl(null),
      hwdpLength: new UntypedFormControl(0, { updateOn: 'blur' }),
      collarLength: new UntypedFormControl(0, { updateOn: 'blur' })
    });

    this._subscriptions.add(
      this.drillstringForm.controls.heavyWeightDrillPipe.valueChanges.subscribe(pipe => {
        this.updateHwdpGradeValidation(pipe);
        this.resetLengthWhenPipeIsCleared('hwdpLength', pipe);
      })
    );
    this._subscriptions.add(
      this.drillstringForm.controls.drillPipe.valueChanges.subscribe(pipe => {
        this.updateDrillPipeGradeValidation(pipe);
      })
    );
    this._subscriptions.add(
      this.drillstringForm.controls.collar.valueChanges.subscribe(collar => {
        this.updateCollarGradeValidation(collar);
        this.resetLengthWhenPipeIsCleared('collarLength', collar);
      })
    );

    this.updateDrillPipeGradeValidation(this.drillstringForm.controls.drillPipe.value);
    this.updateHwdpGradeValidation(this.drillstringForm.controls.heavyWeightDrillPipe.value);
    this.resetLengthWhenPipeIsCleared('hwdpLength', this.drillstringForm.controls.heavyWeightDrillPipe.value);
    this.updateCollarGradeValidation(this.drillstringForm.controls.collar.value);
    this.resetLengthWhenPipeIsCleared('collarLength', this.drillstringForm.controls.collar.value);
  }

  async ngOnInit(): Promise<void> {
    this.isLoading.set(true);
    this._appIds = this._store.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
    const units = await this._store.get<UserUnitsModel>(StorageKeys.UNITS);
    this.longLengthUnit = units.longLengths;
    this._shortLengthUnit = units.shortLengths;
    // this._shortLengthLabel = units.shortLengths;
    this._linearDensityUnit = units.linearDensity;
    this._weightSymbol = unitsLib[units.linearDensity].symbol;
    this._diameterPrecisionOverrides = { [units.shortLengths]: 3 };
    this._weightPrecisionOverrides = { [units.linearDensity]: 3 };

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));
    this.getData();

    if (!this.calledFromSignalR) {
      const debouncedOnChange = this.drillstringForm.valueChanges.pipe(
        debounceTime(500),
        distinctUntilChanged()
      );
      this._subscriptions.add(debouncedOnChange.subscribe(formData => this.onFormChange(formData)));
    }
  }

  private signalRfunc(data: any): void {
    if ([PeriforOnChangeMessages.REFRESH_PIPES_CATALOG, PeriforOnChangeMessages.REFRESH_GRADES_CATALOG, PeriforOnChangeMessages.REFRESH_STRING_INPUTS, PeriforOnChangeMessages.REFRESH_TUBULARS_LIST].includes(data.action)) {
      this.isLoading.set(true);
      this.calledFromSignalR = true;
      this.drillstringForm.reset({ emitEvent: false });
      this.drillstringForm.controls.hwdpLength.setValue(0, { emitEvent: false });
      this.drillstringForm.controls.collarLength.setValue(0, { emitEvent: false });
      this.getData();
    }
  }

  private getData(): void {
    this._wellConfigService.getTubular().subscribe({
      next: (currentString: any) => {
        if (currentString?.id == undefined) {
          this.configMissing = true;
          this.disabledMessage = "Well Configuration Missing: Please specify at least one string";
          this.isLoading.set(false);
          return;
        }

        const sources: Observable<any>[] = [
          this._pipesCatalogService.getPipes(),
          this._drillstringService.getDrillstringForTubular(),
          this._wellConfigService.getTubulars(),
          this._gradesCatalogService.getGrades()
        ];

        forkJoin(sources).pipe(
          map(([pipes, drillstring, tubulars, grades]) => {
            this.isTubingOrTieback = currentString.type == 'Tubing' || currentString.type == 'Tieback';
            this.disabledMessage = this.isTubingOrTieback ? `Not applicable for ${currentString.type.toLowerCase()}` : '';

            // this.toggleFormDisabledState(this.isTubingOrTieback);

            this.drillstringId = drillstring.id;
            this.drillstringForm.controls.tubularStringId.setValue(currentString.id, { emitEvent: false });
            let openHole = currentString.holeSize;

            if (currentString.type === 'Tieback') {
              const linerIndex = tubulars.findIndex(t => t.hangerMd === currentString.shoeMd);
              openHole = tubulars[linerIndex].holeSize;
            }

            this.drillPipes = this.filterPipes(pipes, 'drillPipe', openHole || GetShortLengthValueFromInches(7, this._shortLengthUnit));
            this.hwdps = this.filterPipes(pipes, 'hwdp', openHole);
            this.collars = this.filterPipes(pipes, 'collars', openHole);
            this.grades = grades as GradeCatalog[];
            this.drillPipeGrades = grades.filter(g => g.discriminator === 'DrillPipe');
            this.hwdpGrades = grades.filter(g => g.discriminator === 'Hwdp');
            this.collarGrades = grades.filter(g => g.discriminator === 'Collar');

            this.drillstringForm.patchValue(drillstring);
            this.updateDrillPipeGradeValidation(this.drillstringForm.controls.drillPipe.value);
            this.updateHwdpGradeValidation(this.drillstringForm.controls.heavyWeightDrillPipe.value);
            this.updateCollarGradeValidation(this.drillstringForm.controls.collar.value);
            this.isLoading.set(false);
          }),
          catchError(err => {
            this.isLoading.set(false);
            throw err;
          })
        ).subscribe();
      },
      error: () => {
        this.isLoading.set(false);
      }
    });
  }

  private filterPipes(pipes: PipesCatalog[], discriminator: string, openHole: number): PipesCatalog[] {
    return pipes.filter(p => p.discriminator === discriminator && p.outsideDiameter <= openHole);
  }

  public readonly formatPipeLabel = (pipe: PipesCatalog): string => {
    const diameterSuffix = this._shortLengthUnit === 'in' ? '"' : this._shortLengthUnit;
    const weightSuffix = this._weightSymbol === 'lb/ft' ? '#' : this._weightSymbol;
    const diameter = (value: number) =>
      formatUnitNumber(value, this._shortLengthUnit, this._locale, undefined, this._diameterPrecisionOverrides);
    const weight = formatUnitNumber(
      pipe.weightPerFoot, this._linearDensityUnit, this._locale, undefined, this._weightPrecisionOverrides
    );
    return `OD: ${diameter(pipe.outsideDiameter)}${diameterSuffix}, Weight: ${weight}${weightSuffix}, ID: ${diameter(pipe.insideDiameter)}${diameterSuffix}`;
  };

  public getPipePlaceholder(pipe: PipesCatalog | null | undefined, options: PipesCatalog[]): string {
    if (
      typeof pipe?.outsideDiameter !== 'number' ||
      typeof pipe?.weightPerFoot !== 'number' ||
      typeof pipe?.insideDiameter !== 'number'
    ) {
      return 'Select';
    }
    const label = this.formatPipeLabel(pipe);
    return options.some(option => option.id === pipe.id) ? label : `${label} (local only)`;
  }

  private updateDrillPipeGradeValidation(drillPipe: unknown): void {
    const drillPipeGrade = this.drillstringForm.controls.drillPipeGrade;

    if (drillPipe) {
      drillPipeGrade.setValidators([Validators.minLength(1)]);
    } else {
      drillPipeGrade.clearValidators();
    }

    drillPipeGrade.updateValueAndValidity({ emitEvent: false });
  }

  private updateHwdpGradeValidation(heavyWeightDrillPipe: unknown): void {
    const hwdpGrade = this.drillstringForm.controls.hwdpGrade;
    this.hwdpLengthValidation = { min: heavyWeightDrillPipe ? 1 : 0, max: 10000000 };

    if (heavyWeightDrillPipe) {
      hwdpGrade.setValidators([Validators.minLength(1)]);
    } else {
      hwdpGrade.clearValidators();
    }

    hwdpGrade.updateValueAndValidity({ emitEvent: false });
  }

  private updateCollarGradeValidation(collar: unknown): void {
    const collarGrade = this.drillstringForm.controls.collarGrade;
    this.collarLengthValidation = { min: collar ? 1 : 0, max: 10000000 };

    if (collar) {
      collarGrade.setValidators([Validators.minLength(1)]);
    } else {
      collarGrade.clearValidators();
    }

    collarGrade.updateValueAndValidity({ emitEvent: false });
  }

  private resetLengthWhenPipeIsCleared(lengthControlName: 'hwdpLength' | 'collarLength', pipe: unknown): void {
    if (!pipe) {
      this.drillstringForm.controls[lengthControlName].setValue(0, { emitEvent: false });
    }
  }

  public getGradePlaceholderText(grade: GradeCatalog | null): string {
    if (grade == null) {
      return 'Select Grade';
    }

    const gradeExists = this.grades.some(catalogGrade =>
      catalogGrade.name === grade.name &&
      catalogGrade.minimumApiYieldStrength === grade.minimumApiYieldStrength &&
      catalogGrade.youngsModulus === grade.youngsModulus &&
      catalogGrade.poissonsRatio === grade.poissonsRatio &&
      catalogGrade.thermalExpansionCoefficient === grade.thermalExpansionCoefficient &&
      catalogGrade.ultimateTensileStrength === grade.ultimateTensileStrength &&
      catalogGrade.density === grade.density &&
      catalogGrade.specificHeatCapacity === grade.specificHeatCapacity &&
      catalogGrade.temperatureDeration?.name === grade.temperatureDeration?.name
    );

    return gradeExists ? grade.name : `${grade.name} (local only)`;
  }

  private async onFormChange(formData: DrillstringModel): Promise<void> {
    if (!this.resetData && this.drillstringForm.pristine) {
      return;
    }

    if (this.drillstringForm.valid && !this.isLoading()) {
      formData.designId = this._appIds.designId;

      if (formData.drillPipe == null) {
        formData.drillPipeGrade = null;
      }

      if (formData.collar == null && formData.collarLength > 0) {
        formData.collarLength = 0;
      }

      if (formData.collar == null) {
        formData.collarGrade = null;
      }

      if (formData.heavyWeightDrillPipe == null && formData.hwdpLength > 0) {
        formData.hwdpLength = 0;
      }

      if (formData.heavyWeightDrillPipe == null) {
        formData.hwdpGrade = null;
      }

      if (this.drillstringId) {
        formData.id = this.drillstringId;
        await lastValueFrom(this._drillstringService.updateDrillstringForTubular(formData));
      } else {
        const res = await lastValueFrom(this._drillstringService.setDrillstring(formData));
        this.drillstringId = res.toString();
      }
      this._messenger.publish(new DataSavedMessage('Drill String'));
    }
    this.resetData = false;
  }

  public resetDrillPipe(): void {
    this.resetData = true;
    this.drillstringForm.controls.drillPipeGrade.setValue(null, { emitEvent: false });
    this.drillstringForm.controls.drillPipe.setValue(null);
    this.updateDrillPipeGradeValidation(null);
  }

  public resetHwdp(): void {
    this.resetData = true;
    this.drillstringForm.controls.heavyWeightDrillPipe.setValue(null, { emitEvent: false });
    this.drillstringForm.controls.hwdpGrade.setValue(null, { emitEvent: false });
    this.drillstringForm.controls.hwdpLength.setValue(0);
    this.updateHwdpGradeValidation(null);
  }

  public resetCollar(): void {
    this.resetData = true;
    this.drillstringForm.controls.collar.setValue(null, { emitEvent: false });
    this.drillstringForm.controls.collarGrade.setValue(null, { emitEvent: false });
    this.drillstringForm.controls.collarLength.setValue(0);
    this.updateCollarGradeValidation(null);
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
